import { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, Building2, Home } from "lucide-react";
import {
  PROJECT_TYPES_BY_CLASS,
  type PropertyClass,
  type ProjectType,
  getSubtrades,
  projectTypeLabel,
} from "@/lib/gcSubtrades";
import { cn } from "@/lib/utils";

export interface GCScope {
  propertyClass: PropertyClass | null;
  projectType: ProjectType | null;
  selectedSubtrades: string[];
}

interface Props {
  value: GCScope;
  onChange: (next: GCScope) => void;
}

export function GCJobScopeBuilder({ value, onChange }: Props) {
  const { propertyClass, projectType, selectedSubtrades } = value;

  const baseSubtrades = useMemo(
    () => (propertyClass && projectType ? getSubtrades(propertyClass, projectType) : []),
    [propertyClass, projectType]
  );

  // Custom subtrades the user adds on the fly for this job
  const [customSubtrades, setCustomSubtrades] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState("");

  // All subtrades visible (predefined + custom), de-duped
  const allSubtrades = useMemo(() => {
    const seen = new Set<string>();
    return [...baseSubtrades, ...customSubtrades].filter((s) => {
      const k = s.toLowerCase().trim();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [baseSubtrades, customSubtrades]);

  const setClass = (c: PropertyClass) => {
    onChange({ propertyClass: c, projectType: null, selectedSubtrades: [] });
    setCustomSubtrades([]);
  };

  const setProj = (p: ProjectType) => {
    onChange({ propertyClass, projectType: p, selectedSubtrades: [] });
    setCustomSubtrades([]);
  };

  const toggleSubtrade = (name: string) => {
    const has = selectedSubtrades.includes(name);
    onChange({
      ...value,
      selectedSubtrades: has
        ? selectedSubtrades.filter((s) => s !== name)
        : [...selectedSubtrades, name],
    });
  };

  const addCustom = () => {
    const name = customInput.trim();
    if (!name) return;
    if (allSubtrades.some((s) => s.toLowerCase() === name.toLowerCase())) {
      setCustomInput("");
      return;
    }
    setCustomSubtrades((prev) => [...prev, name]);
    onChange({ ...value, selectedSubtrades: [...selectedSubtrades, name] });
    setCustomInput("");
  };

  const removeCustom = (name: string) => {
    setCustomSubtrades((prev) => prev.filter((s) => s !== name));
    onChange({
      ...value,
      selectedSubtrades: selectedSubtrades.filter((s) => s !== name),
    });
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Property class */}
      <div className="space-y-2">
        <Label>
          Property Type <span className="text-destructive">*</span>
        </Label>
        <div className="grid grid-cols-2 gap-3">
          {([
            { val: "residential", label: "Residential", Icon: Home },
            { val: "commercial", label: "Commercial", Icon: Building2 },
          ] as const).map(({ val, label, Icon }) => {
            const active = propertyClass === val;
            return (
              <Button
                key={val}
                type="button"
                variant={active ? "default" : "outline"}
                onClick={() => setClass(val)}
                className={cn(
                  "h-14 justify-center gap-2 text-base font-semibold rounded-lg shadow-sm transition-all",
                  active && "ring-2 ring-primary/40 shadow-md"
                )}
              >
                <Icon className="h-5 w-5" />
                {label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Step 2: Project type */}
      {propertyClass && (
        <div className="space-y-2">
          <Label>
            Project Type <span className="text-destructive">*</span>
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {PROJECT_TYPES_BY_CLASS[propertyClass].map((p) => {
              const active = projectType === p.value;
              return (
                <Button
                  key={p.value}
                  type="button"
                  variant={active ? "default" : "outline"}
                  onClick={() => setProj(p.value)}
                  className={cn(
                    "h-12 justify-center text-sm font-semibold rounded-lg shadow-sm transition-all",
                    active && "ring-2 ring-primary/40 shadow-md"
                  )}
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: Subtrades */}
      {propertyClass && projectType && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <Label>Subtrades / Scope</Label>
            <span className="text-xs text-muted-foreground">
              {selectedSubtrades.length} selected
            </span>
          </div>
          <p className="text-sm text-muted-foreground -mt-1">
            Check every trade involved in this {projectTypeLabel(projectType).toLowerCase()} project.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
            {allSubtrades.map((name) => {
              const checked = selectedSubtrades.includes(name);
              const isCustom = customSubtrades.includes(name);
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleSubtrade(name)}
                  className={cn(
                    "group relative h-11 px-3 rounded-lg border text-sm font-medium",
                    "flex items-center justify-between gap-2 shadow-sm transition-all",
                    "hover:shadow-md hover:-translate-y-px",
                    checked
                      ? "bg-primary text-primary-foreground border-primary ring-2 ring-primary/30"
                      : "bg-background border-border hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <span className="truncate">{name}</span>
                  {isCustom && (
                    <span
                      role="button"
                      tabIndex={-1}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustom(name);
                      }}
                      className={cn(
                        "shrink-0 rounded-full p-0.5 transition-colors",
                        checked
                          ? "hover:bg-primary-foreground/20"
                          : "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      )}
                      aria-label={`Remove ${name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Add custom subtrade on the fly */}
          <div className="flex gap-2 pt-1">
            <Input
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustom();
                }
              }}
              placeholder="Add a subtrade not listed (e.g. Solar, Pool, Smart Home)"
              className="flex-1"
            />
            <Button type="button" variant="outline" onClick={addCustom} disabled={!customInput.trim()}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
